import React, { useState, useEffect } from 'react';

interface Transport {
  id: number;
  title: string;
  type: string;
  description: string;
  origin: string;
  destination: string;
  contact_phone: string;
  status: string;
  user_name?: string;
  created_at: string;
}

const Transports: React.FC = () => {
  const [transports, setTransports] = useState<Transport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadTransports = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch('https://fellfell.ir/api/transport.php', {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      console.log('📦 درخواست‌ها دریافت شد:', data);
      
      if (data.success && Array.isArray(data.data)) {
        setTransports(data.data);
      } else {
        setError('خطا در دریافت درخواست‌ها: ساختار داده نامعتبر');
      }
    } catch (err: any) {
      console.error('❌ خطا:', err);
      setError('خطا در ارتباط با سرور: ' + err.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadTransports();
  }, []);

  const handleStatusChange = async (id: number, status: string) => {
    try {
      const response = await fetch(`https://fellfell.ir/api/transport.php?id=${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status })
      });
      const data = await response.json();
      if (data.success) {
        showToast(`وضعیت درخواست به ${status === 'active' ? 'فعال' : status === 'completed' ? 'انجام شده' : 'لغو شده'} تغییر یافت`, 'success');
        loadTransports();
      } else {
        showToast('خطا در تغییر وضعیت: ' + (data.message || 'خطای نامشخص'), 'error');
      }
    } catch (err: any) {
      showToast('خطا در تغییر وضعیت: ' + err.message, 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('آیا از حذف این درخواست مطمئن هستید؟')) return;
    try {
      const response = await fetch(`https://fellfell.ir/api/transport.php?id=${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        showToast('درخواست با موفقیت حذف شد', 'success');
        loadTransports();
      } else {
        showToast('خطا در حذف درخواست: ' + (data.message || 'خطای نامشخص'), 'error');
      }
    } catch (err: any) {
      showToast('خطا در حذف درخواست: ' + err.message, 'error');
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; color: string; bg: string }> = {
      'active': { label: '✅ فعال', color: '#2e7d32', bg: '#e8f5e9' },
      'completed': { label: '✅ انجام شده', color: '#1565c0', bg: '#e3f2fd' },
      'cancelled': { label: '❌ لغو شده', color: '#c62828', bg: '#ffebee' }
    };
    return statusMap[status] || { label: status, color: '#333', bg: '#f5f5f5' };
  };

  if (loading) {
    return <div style={{ direction: 'rtl', textAlign: 'center', padding: '50px' }}>⏳ در حال بارگذاری...</div>;
  }

  if (error) {
    return (
      <div style={{ direction: 'rtl', padding: '20px' }}>
        <div style={{ background: '#ffebee', color: '#c62828', padding: '20px', borderRadius: '12px' }}>
          ❌ {error}
          <button onClick={loadTransports} style={{ marginRight: '15px', padding: '5px 15px', background: '#c62828', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
            تلاش مجدد
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ direction: 'rtl', padding: '20px' }}>
      {toast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '14px 28px',
          borderRadius: '12px',
          fontSize: '15px',
          zIndex: 9999,
          background: toast.type === 'success' ? '#2e7d32' : '#c62828',
          color: '#fff',
          boxShadow: '0 6px 25px rgba(0,0,0,0.25)',
          animation: 'slideDown 0.3s ease-out'
        }}>
          {toast.message}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ color: '#1e3a1e' }}>🚚 مدیریت بار و ماشین</h1>
        <span style={{ color: '#888' }}>{transports.length} درخواست</span>
      </div>

      {transports.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', background: '#fff', borderRadius: '12px' }}>
          <p style={{ fontSize: '18px', color: '#888' }}>🚚 هیچ درخواستی ثبت نشده است</p>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: '12px', overflow: 'auto', boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
            <thead>
              <tr style={{ background: '#f5faf5' }}>
                <th style={{ padding: '12px 15px', textAlign: 'right' }}>عنوان</th>
                <th style={{ padding: '12px 15px', textAlign: 'right' }}>نوع</th>
                <th style={{ padding: '12px 15px', textAlign: 'right' }}>مبدا</th>
                <th style={{ padding: '12px 15px', textAlign: 'right' }}>مقصد</th>
                <th style={{ padding: '12px 15px', textAlign: 'right' }}>کاربر</th>
                <th style={{ padding: '12px 15px', textAlign: 'right' }}>وضعیت</th>
                <th style={{ padding: '12px 15px', textAlign: 'center' }}>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {transports.map((t) => {
                const status = getStatusBadge(t.status || 'active');
                return (
                  <tr key={t.id} style={{ borderBottom: '1px solid #eef5ee' }}>
                    <td style={{ padding: '12px 15px', fontWeight: '500' }}>{t.title}</td>
                    <td style={{ padding: '12px 15px' }}>{t.type === 'load' ? 'بار' : 'ماشین'}</td>
                    <td style={{ padding: '12px 15px' }}>{t.origin || 'نامشخص'}</td>
                    <td style={{ padding: '12px 15px' }}>{t.destination || 'نامشخص'}</td>
                    <td style={{ padding: '12px 15px' }}>{t.user_name || 'کاربر'}</td>
                    <td style={{ padding: '12px 15px' }}>
                      <select
                        value={t.status || 'active'}
                        onChange={(e) => handleStatusChange(t.id, e.target.value)}
                        style={{
                          padding: '5px 10px',
                          borderRadius: '6px',
                          border: '1px solid #ddd',
                          background: status.bg,
                          color: status.color,
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="active">✅ فعال</option>
                        <option value="completed">✅ انجام شده</option>
                        <option value="cancelled">❌ لغو شده</option>
                      </select>
                    </td>
                    <td style={{ padding: '12px 15px', textAlign: 'center' }}>
                      <button onClick={() => handleDelete(t.id)} style={{ padding: '6px 12px', background: '#ffebee', color: '#c62828', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                        🗑️
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default Transports;